<template>
    <section class="app-client-window">

        <template v-if="mode === 'dashboard'">

            <div class="stats-summary-row">
                <div class="review-alerts">
                    <button v-if="reviewCounts.requests" @click="$emit('review-new', 'requests')">{{ reviewCounts.requests }} New Requests →</button>
                    <button v-if="reviewCounts.submissions" @click="$emit('review-new', 'submissions')">{{ reviewCounts.submissions }} New Submissions →</button>
                </div>
                <span v-if="lastRefreshedAt" class="refresh-time">Updated {{ formatDate(lastRefreshedAt) }}</span>
            </div>

            <div v-if="loadingDashboard" class="state-card dashboard-loading-card">
                Refreshing App Client dashboard...
            </div>

            <div v-else-if="dashboardError" class="state-card error">
                {{ dashboardError }}
            </div>

            <section v-else class="metric-grid">

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            groups
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            App Clients
                        </span>

                        <strong class="metric-value">
                            {{ dashboardCounts.clients }}
                        </strong>

                        <span class="metric-pill active">
                            30 Day Activity:
                            {{ dashboardCounts.activeClients30Days }}
                            /
                            {{ dashboardCounts.createdClients }}
                            ·
                            {{
                                formatPercent(
                                    dashboardCounts.activeClients30DayPercent
                                )
                            }}
                        </span>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.clients?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.clients?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.clients?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            gavel
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            Litigation Applications
                        </span>

                        <strong class="metric-value">
                            {{
                                dashboardCounts
                                    .litigationApplicationsStarted
                            }}
                        </strong>

                        <div class="metric-pills">

                            <span class="metric-pill">
                                Started:
                                {{
                                    dashboardCounts
                                        .litigationApplicationsStarted
                                }}
                            </span>

                            <span class="metric-pill active">
                                Completed:
                                {{
                                    dashboardCounts
                                        .litigationApplicationsCompleted
                                }}
                            </span>

                        </div>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.litigation?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.litigation?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.litigation?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            visibility
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            Watchlist Sessions
                        </span>

                        <strong class="metric-value">
                            {{
                                dashboardCounts
                                    .watchlistSessionsStarted
                            }}
                        </strong>

                        <div class="metric-pills">

                            <span class="metric-pill">
                                Started:
                                {{
                                    dashboardCounts
                                        .watchlistSessionsStarted
                                }}
                            </span>

                            <span class="metric-pill active">
                                Submitted:
                                {{
                                    dashboardCounts
                                        .watchlistSessionsCompleted
                                }}
                            </span>

                        </div>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.watchlist?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.watchlist?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.watchlist?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            auto_awesome
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            Maix Conversations
                        </span>

                        <strong class="metric-value">
                            {{
                                dashboardCounts
                                    .maixChatsStarted
                            }}
                        </strong>

                        <div class="metric-pills">

                            <span class="metric-pill">
                                Started:
                                {{
                                    dashboardCounts
                                        .maixChatsStarted
                                }}
                            </span>

                            <span class="metric-pill active">
                                Activated:
                                {{
                                    dashboardCounts
                                        .maixChatsActivated
                                }}
                            </span>

                        </div>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.maix?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.maix?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.maix?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            forum
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            Watch Conversations
                        </span>

                        <strong class="metric-value">
                            {{
                                dashboardCounts
                                    .watchChatsStarted
                            }}
                        </strong>

                        <div class="metric-pills">

                            <span class="metric-pill">
                                Started:
                                {{
                                    dashboardCounts
                                        .watchChatsStarted
                                }}
                            </span>

                            <span class="metric-pill active">
                                Activated:
                                {{
                                    dashboardCounts
                                        .watchChatsActivated
                                }}
                            </span>

                        </div>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.watch?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.watch?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.watch?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

                <article class="metric-card">

                    <span class="metric-icon">
                        <span class="material-icons-outlined">
                            inbox
                        </span>
                    </span>

                    <div class="metric-content">

                        <span class="metric-label">
                            Attorney Requests
                        </span>

                        <strong class="metric-value">
                            {{
                                dashboardCounts
                                    .attorneyRequests
                            }}
                        </strong>

                        <div class="metric-pills">

                            <!-- <span class="metric-pill">
                    Total:
                    {{
                        dashboardCounts
                        .attorneyRequests
                    }}
                    </span> -->

                            <span class="metric-pill request-alert-pill">
                                New and Unanswered:
                                {{
                                    dashboardCounts
                                        .newAttorneyRequests
                                }}
                            </span>

                        </div>

                    </div>

                    <div v-if="statsExpanded" class="stat-additions">
                        <span v-if="statsLoading">Loading additions…</span>
                        <template v-else>
                            <span>Today <strong>{{ statAdditions.requests?.today ?? '—' }}</strong></span>
                            <span>Past Week <strong>{{ statAdditions.requests?.week ?? '—' }}</strong></span>
                            <span>Past Month <strong>{{ statAdditions.requests?.month ?? '—' }}</strong></span>
                        </template>
                    </div>
                </article>

            </section>

            <div class="stats-controls stats-footer">
                <button type="button" :aria-expanded="statsExpanded" @click="toggleStats">{{ statsExpanded ? 'Collapse stats' : 'Expand stats' }}</button>
            </div>
            <p v-if="statsExpanded" class="stats-explanation">New records · Today (local time), past 7 days, past 30 days. Existing records only.</p>
            <p v-if="statsExpanded && statsError" role="alert">{{ statsError }} <button type="button" @click="loadStatAdditions">Retry</button></p>

            <div class="dashboard-support-divider" aria-hidden="true"></div>

            <section class="support-section">

                <div class="support-heading">
                <h2>Support Tickets</h2>

                <p>
                    Find a ticket by code or review recent App Client support requests.
                </p>
                </div>

                <section class="search-panel">

                <div class="section-heading">
                    <div>
                    <h3>Find Support Ticket</h3>
                    <p>Enter the six-digit code provided by the App Client.</p>
                    </div>
                </div>

                <form class="client-search-form" @submit.prevent="submitSearch">

                    <div class="search-field">
                    <span class="material-icons-outlined">confirmation_number</span>

                    <input
                        v-model="searchText"
                        type="text"
                        inputmode="numeric"
                        autocomplete="off"
                        maxlength="6"
                        placeholder="Enter 6-digit ticket code"
                        @input="sanitizeTicketCode"
                    />
                    </div>

                    <button
                    type="submit"
                    class="search-button"
                    :disabled="searching || !canSearch"
                    >
                    {{ searching ? "Searching..." : "Search Ticket" }}
                    </button>

                </form>

                <p v-if="searchError" class="search-error">
                    {{ searchError }}
                </p>

                </section>

                <section class="ticket-filter-section">

                    <div class="section-heading">
                    <div>
                        <h3>Filter Tickets</h3>
                        <p>Load additional Support Tickets by status and time period.</p>
                    </div>
                    </div>

                    <div class="ticket-filter-bar">

                    <label class="filter-field">
                        <span>Status</span>

                        <select v-model="ticketStatusFilter">
                        <option
                            v-for="option in ticketStatusOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Time Period</span>

                        <select v-model="ticketTimeFilter">
                        <option
                            v-for="option in ticketTimeOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </option>
                        </select>
                    </label>

                    <div class="filter-spacer"></div>

                    <button
                        type="button"
                        class="get-tickets-button"
                        :disabled="loadingFilteredTickets"
                        @click="getFilteredTickets"
                    >
                        {{ loadingFilteredTickets ? "Loading..." : "Get Tickets" }}
                    </button>

                    <button
                        v-if="showingFilteredTickets"
                        type="button"
                        class="clear-filters-button"
                        :disabled="loadingFilteredTickets"
                        aria-label="Clear filters"
                        title="Clear filters"
                        @click="returnToNewTickets"
                    >
                        <span class="material-icons-outlined">close</span>
                    </button>

                    </div>

                    </section>

                <section class="ticket-list-section">

                <div class="section-heading ticket-list-header">

                    <div>
                    <h3>{{ displayedTicketHeading }}</h3>

                    <p>
                        {{ displayedTicketDescription }}
                    </p>
                    </div>

                    <div class="ticket-list-actions">
                        <button
                            v-if="showingSearchResults"
                            type="button"
                            class="back-to-new-button"
                            @click="clearTicketSearch"
                        >
                            Back to New Tickets
                        </button>

                        <span class="ticket-count-pill">
                        {{ displayedTickets.length }}
                        {{ showingSearchResults || showingFilteredTickets ? "Found" : "Unopened" }}
                        </span>
                    </div>

                </div>

                <div
                    v-if="!showingSearchResults && filteredTicketError"
                    class="state-card error"
                    >
                    {{ filteredTicketError }}
                    </div>

                    <div
                    v-else-if="displayedTickets.length === 0"
                    class="state-card"
                    >
                    {{
                        showingSearchResults
                        ? "No Support Tickets matched this code."
                        : showingFilteredTickets
                        ? "No Support Tickets matched these filters."
                        : "No unopened Support Tickets."
                    }}
                    </div>

                    <div v-else class="ticket-list">

                    <button
                        v-for="ticket in displayedTickets"
                    :key="ticket.id"
                    type="button"
                    class="ticket-row"
                    @click="openTicket(ticket)"
                    >

                    <div class="ticket-code-block">

                        <span class="ticket-label">
                        Ticket
                        </span>

                        <strong class="ticket-code">
                        #{{ ticket.code }}
                        </strong>

                    </div>

                    <div class="ticket-issue-block">

                        <span class="ticket-label">
                        Issue
                        </span>

                        <strong class="ticket-issue">
                        {{ ticket.issue || "No issue provided." }}
                        </strong>

                    </div>

                    <div class="ticket-status-block">

                        <span
                            class="status-pill"
                            :class="ticketStatusClass(ticket)"
                        >
                        {{ ticketStatusLabel(ticket) }}
                        </span>

                        <span class="ticket-age">
                        {{ formatTicketAge(ticket.createdAt) }}
                        </span>

                    </div>

                    <span class="material-icons-outlined ticket-chevron">
                        chevron_right
                    </span>

                    </button>

                </div>

                </section>

                </section>

                <UserExitChats :refresh-key="refreshKey" />

                <MaixFooter />

        </template>
    </section>
</template>

<script>
import {
    computed,
    onMounted,
    ref,
    watch
} from "vue";

import {
    fetchAppClientDashboard,
    fetchAppClientStatAdditions,
    fetchSupportTickets,
    fetchSupportTicketsByCode
} from "@/firebase/firestore";

import MaixFooter from "@/components/utils/maixFooter.vue";
import UserExitChats from "@/components/appClient/UserExitChats.vue";

export default {
    name:
        "AppClientDetails",

    components: {
        MaixFooter,
        UserExitChats
    },

    props: {
        reviewCounts: { type: Object, default: () => ({}) },
        mode: {
            type:
                String,
            default:
                "dashboard"
        },

        client: {
            type:
                Object,
            default:
                null
        },

        refreshKey: {
            type:
                Number,
            default:
                0
        }, 
    },

    emits: [
        "open-ticket", "review-new", "refresh-reviews"
    ],

    setup(
    props,
    { emit }
    ) {

        const searchText =
            ref("");

        const searching =
            ref(false);

        const searchError =
            ref("");

        const searchedTickets =
            ref([]);

        const showingSearchResults =
            ref(false);

        const loadingDashboard =
            ref(false);

        const dashboardError =
            ref("");

        const lastRefreshedAt =
            ref(null);

        /*
         These values remain cached in this component.
         They change only when refreshKey changes.
         */

        const unopenedTickets = ref([]);

        const ticketStatusFilter = ref("needs-action");
        const ticketTimeFilter = ref("30-days");
        const filteredTickets = ref([]);
        const loadingFilteredTickets = ref(false);
        const showingFilteredTickets = ref(false);
        const filteredTicketError = ref("");

        const ticketStatusOptions = [
            { value: "needs-action", label: "Needs Action" },
            { value: "new", label: "New" },
            { value: "open", label: "Open" },
            { value: "replied", label: "Replied" },
            { value: "pending", label: "Pending" },
            { value: "resolved", label: "Resolved" },
            { value: "closed", label: "Closed" },
            { value: "all", label: "All" }
            ];

            const ticketTimeOptions = [
            { value: "7-days", label: "7 Days" },
            { value: "30-days", label: "30 Days" },
            { value: "90-days", label: "90 Days" },
            { value: "all-time", label: "All Time" }
            ];

        const statsExpanded = ref(false), statsLoading = ref(false), statsError = ref("");
        const statAdditions = ref({});
        const loadStatAdditions = async () => {
            if (statsLoading.value) return;
            statsLoading.value = true; statsError.value = "";
            try { statAdditions.value = (await fetchAppClientStatAdditions()).counts; }
            catch { statsError.value = "Unable to load additions. Check that the stats function is deployed."; }
            finally { statsLoading.value = false; }
        };
        const toggleStats = () => {
            statsExpanded.value = !statsExpanded.value;
            if (statsExpanded.value && !Object.keys(statAdditions.value).length) loadStatAdditions();
        };

        const dashboardCounts =
            ref({
                createdClients:
                    0,

                clients:
                    0,

                archivedClients:
                    0,

                activeClients30Days:
                    0,

                activeClients30DayPercent:
                    0,

                litigationApplicationsStarted:
                    0,

                litigationApplicationsCompleted:
                    0,

                watchlistSessionsStarted:
                    0,

                watchlistSessionsCompleted:
                    0,

                maixChatsStarted:
                    0,

                maixChatsActivated:
                    0,

                watchChatsStarted:
                    0,

                watchChatsActivated:
                    0,

                attorneyRequests:
                    0,

                newAttorneyRequests:
                    0
            });

        const canSearch =
            computed(() =>
                /^\d{6}$/.test(searchText.value)
            );

        const sanitizeTicketCode =
            () => {

                searchText.value = String(searchText.value ?? "")
                    .replace(/\D/g, "")
                    .slice(0, 6);
            };

        const submitSearch =
            async () => {

                if (!canSearch.value || searching.value) { return; }

                searching.value = true;
                searchError.value = "";

                try {

                const code = searchText.value.trim();

                console.log(
                    "🔎 Searching Support Ticket:",
                    code
                );

                searchedTickets.value =
                    await fetchSupportTicketsByCode(code);

                showingSearchResults.value = true;
                showingFilteredTickets.value = false;
                filteredTickets.value = [];
                filteredTicketError.value = "";

                } catch (error) {

                console.error(
                    "❌ Unable to search Support Tickets:",
                    error
                );

                searchedTickets.value = [];
                showingSearchResults.value = true;
                showingFilteredTickets.value = false;
                filteredTickets.value = [];
                filteredTicketError.value = "";

                searchError.value =
                    error?.message ??
                    "Unable to search Support Tickets.";

                } finally {

                searching.value = false;
                }
            };

        const refreshDashboard =
            async () => {

                if (loadingDashboard.value) {
                    return;
                }

                loadingDashboard.value =
                    true;

                dashboardError.value =
                    "";

                try {

                    const result =
                        await fetchAppClientDashboard();

                    emit("refresh-reviews");
                    statAdditions.value = {};
                    if (statsExpanded.value) await loadStatAdditions();
                    dashboardCounts.value =
                        result.counts;

                    unopenedTickets.value = 
                        result.supportTickets?.unopened ?? [];

                    lastRefreshedAt.value =
                        result.lastUpdatedAt ??
                        new Date();

                    console.log(
                        "✅ App Client dashboard loaded:",
                        result.raw
                    );

                } catch (error) {

                    console.error(
                        "❌ Unable to refresh App Client dashboard:",
                        error
                    );

                    dashboardError.value =
                        "Unable to refresh the dashboard.";

                } finally {

                    loadingDashboard.value =
                        false;
                }
            };

            const getFilteredTickets =
                async () => {

                    if (loadingFilteredTickets.value) { return; }

                    showingSearchResults.value = false;
                    searchedTickets.value = [];
                    searchError.value = "";
                    loadingFilteredTickets.value = true;
                    filteredTicketError.value = "";

                    try {

                    console.log(
                        "📥 Calling fetchSupportTickets:",
                        {
                        status: ticketStatusFilter.value,
                        time: ticketTimeFilter.value
                        }
                    );

                    const tickets =
                        await fetchSupportTickets({
                        statusFilter: ticketStatusFilter.value,
                        timeFilter: ticketTimeFilter.value
                        });

                    console.log(
                        "📦 fetchSupportTickets returned:",
                        tickets
                    );

                    filteredTickets.value = tickets;
                    showingFilteredTickets.value = true;

                    } catch (error) {

                    console.error(
                        "❌ Unable to load filtered Support Tickets:",
                        error
                    );

                    filteredTicketError.value =
                        error?.message ??
                        "Unable to load Support Tickets.";

                    } finally {

                    loadingFilteredTickets.value = false;
                    }
                };

        const returnToNewTickets =
            () => {

                showingFilteredTickets.value = false;
                filteredTickets.value = [];
            };

        const clearTicketSearch =
            () => {

                showingSearchResults.value = false;
                searchedTickets.value = [];
                searchError.value = "";
                showingFilteredTickets.value = false;
                filteredTickets.value = [];
                filteredTicketError.value = "";
            };

        const displayedTickets =
            computed(() => {

                if (showingSearchResults.value) {
                    return searchedTickets.value;
                }

                if (showingFilteredTickets.value) {
                    return filteredTickets.value;
                }

                return unopenedTickets.value;
            });

        const displayedTicketHeading =
            computed(() => {

                if (showingSearchResults.value) {
                    return `Search Results for #${searchText.value}`;
                }

                if (!showingFilteredTickets.value) {
                    return "New Support Tickets";
                }

                const status =
                ticketStatusOptions.find(
                    option =>
                    option.value ===
                    ticketStatusFilter.value
                );

                return `${status?.label ?? "Filtered"} Support Tickets`;
            });

        const displayedTicketDescription =
            computed(() => {

                if (showingSearchResults.value) {
                    return "All Support Tickets matching the entered code.";
                }

                if (showingFilteredTickets.value) {
                    return "Support Tickets matching the selected filters.";
                }

                return "Support requests that have not yet been opened by an administrator.";
            });

        const ticketStatusClass =
            ticket =>
                ticket.status === "open" && !ticket.openedAt
                    ? "new"
                    : ticket.status || "unknown";

        const ticketStatusLabel =
            ticket => {
                const status = ticketStatusClass(ticket);
                return status.charAt(0).toUpperCase() + status.slice(1);
            };

        const formatDate =
            value => {

                if (!value) {
                    return "";
                }

                const date =
                    value instanceof Date
                        ? value
                        : new Date(value);

                if (
                    Number.isNaN(
                        date.getTime()
                    )
                ) {

                    return "";
                }

                return date.toLocaleString();
            };

        const formatPercent =
            value => {

                const number =
                    Number(value);

                if (
                    !Number.isFinite(number)
                ) {
                    return "0%";
                }

                return `${number.toFixed(1)}%`;
            };

        const openTicket =
            ticket => {

                console.log(
                "🎫 Open Support Ticket:",
                ticket
                );

                emit(
                "open-ticket",
                ticket
                );
            };

        const formatTicketAge =
            value => {

                const date = value?.toDate?.() ?? (value instanceof Date ? value : null);
                if (!date) { return ""; }

                const minutes = Math.floor((Date.now() - date.getTime()) / 60000);

                if (minutes < 1) { return "Just now"; }
                if (minutes < 60) { return `${minutes}m ago`; }

                const hours = Math.floor(minutes / 60);
                if (hours < 24) { return `${hours}h ago`; }

                const days = Math.floor(hours / 24);
                return `${days}d ago`;
            };

        onMounted(
            refreshDashboard
        );

        watch(
            () => props.refreshKey,
            refreshDashboard
        );

        return {
            statsExpanded, statsLoading, statsError, statAdditions, toggleStats, loadStatAdditions,
            searchText,
            searching,
            searchError,
            searchedTickets,
            showingSearchResults,

            loadingDashboard,
            dashboardError,
            lastRefreshedAt,
            dashboardCounts,
            unopenedTickets,
            openTicket,

            ticketStatusFilter,
            ticketTimeFilter,
            ticketStatusOptions,
            ticketTimeOptions,
            ticketStatusClass,
            ticketStatusLabel,

            filteredTickets,
            loadingFilteredTickets,
            showingFilteredTickets,
            filteredTicketError,

            displayedTickets,
            displayedTicketHeading,
            displayedTicketDescription,
            getFilteredTickets,
            returnToNewTickets,
            clearTicketSearch,

            canSearch,
            sanitizeTicketCode,
            submitSearch,
            formatDate,
            formatPercent,
            formatTicketAge
        };
    }
};

</script>

<style scoped>
.app-client-window {
    display: flex;
    flex-direction: column;
    gap: 22px;
    min-height: 0;
}

/* Search */

.search-panel {
    width: 100%;
}

.client-search-form {
    display: grid;
    grid-template-columns:
        minmax(0, 1fr) auto;
    gap: 12px;
    width: 100%;
}

.search-field {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 0 14px;
    box-sizing: border-box;
    border: 1px solid #d5ddd8;
    border-radius: 11px;
    background: #ffffff;
    color: #87918c;
}

.search-field input {
    width: 100%;
    padding: 13px 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #223029;
    font: inherit;
}

.search-button,
.open-client-button {
    padding: 11px 18px;
    border: 0;
    border-radius: 10px;
    background: #18ab69;
    color: #ffffff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
}

.search-button:hover:not(:disabled),
.open-client-button:hover {
    background: #15945c;
}

.search-button:disabled {
    cursor: not-allowed;
    opacity: 0.52;
}

.search-error {
    margin: 9px 0 0;
    color: #a33636;
}

/* Found client */

.found-client-card {
    padding: 20px;
    border: 1px solid #dce4df;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 5px 18px rgba(29, 49, 39, 0.04);
}

.found-client-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
}

.result-label,
.client-eyebrow {
    color: #77837c;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.found-client-heading h3 {
    margin: 5px 0 0;
    color: #1f2d26;
}

.found-client-details {
    display: grid;
    grid-template-columns:
        repeat(4,
            minmax(0, 1fr));
    gap: 12px;
    margin-top: 18px;
}

.found-client-details>div {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    padding: 13px;
    border: 1px solid #e6ebe8;
    border-radius: 9px;
    background: #fafcfa;
}

.found-client-details span {
    color: #7d8782;
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
}

.found-client-details strong {
    color: #2b3831;
}

.breakable {
    overflow-wrap: anywhere;
}

/* Dashboard */

.dashboard-refresh-meta {
    display: flex;
    justify-content: flex-end;
}

.refresh-time {
    color: #818b86;
    font-size: 0.78rem;
}

.metric-grid {
    display: grid;
    grid-template-columns:
        repeat(3,
            minmax(0, 1fr));
    gap: 16px;
}

.metric-card {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 15px;
    min-height: 96px;
    padding: 18px;
    border: 1px solid #dde4e0;
    border-radius: 13px;
    background: #ffffff;
    box-shadow: 0 5px 18px rgba(29, 49, 39, 0.04);
}

.metric-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 43px;
    height: 43px;
    flex-shrink: 0;
    border-radius: 11px;
    background: #edf7f1;
    color: #178554;
}

.metric-label {
    display: block;
    color: #748079;
    font-size: 0.78rem;
    font-weight: 700;
}

.metric-value {
    display: block;
    margin-top: 3px;
    color: #1f2d26;
    font-size: 1.55rem;
}

.state-card,
.client-placeholder {
    padding: 24px;
    border: 1px solid #dfe5e1;
    border-radius: 13px;
    background: #ffffff;
    color: #737d78;
}

.state-card.error {
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
}

.dashboard-loading-card {
    border-color: #d9ebe0;
    background: #f2f8f4;
    color: #159c62;
}

.client-placeholder h3 {
    margin: 6px 0;
    color: #1f2d26;
}

.metric-content {
    min-width: 0;
}

.metric-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.metric-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    background: #f0f2f1;
    color: #66716b;
    font-size: 0.7rem;
    font-weight: 700;
}

.metric-pill.active {
    background: #edf7f1;
    color: #178554;
}

.request-alert-pill {
    background: #fff0e8;
    color: #a35220;
}

.dashboard-support-divider {
  height: 1px;
  margin: 4px 0 0;
  background: #dfe5e1;
}

.search-field input {
  font-size: 1rem;
  letter-spacing: 0.08em;
}

.support-section {
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin-top: 4px;
}

.support-heading h2 {
  margin: 0;
  color: #1f2d26;
  font-size: 1.45rem;
  font-weight: 600;
}

.support-heading p {
  margin: 5px 0 0;
  color: #7b8580;
  font-size: 0.88rem;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 12px;
}

.section-heading h3 {
  margin: 0;
  color: #1f2d26;
  font-size: 1rem;
  font-weight: 600;
}

.section-heading p {
  margin: 4px 0 0;
  color: #7b8580;
  font-size: 0.84rem;
}

.search-panel {
  width: 100%;
}

.ticket-list-section {
  display: flex;
  flex-direction: column;
}

.ticket-list-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-to-new-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #178554;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.back-to-new-button:hover {
  text-decoration: underline;
}

.ticket-count-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 999px;
  background: #fff0e8;
  color: #a35220;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.ticket-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ticket-row {
  display: grid;
  grid-template-columns:
    minmax(110px, 0.7fr)
    minmax(0, 4fr)
    minmax(140px, 0.8fr)
    28px;

  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #dfe5e1;
  border-radius: 12px;
  background: #f8faf9;
  box-shadow: 0 4px 14px rgba(29, 49, 39, 0.025);
  text-align: left;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;
}

.ticket-row:hover {
  background: #f3f7f5;
  border-color: #cfd9d3;
  transform: translateY(-1px);
}

.ticket-code-block,
.ticket-issue-block,
.ticket-status-block {
  min-width: 0;
}

.ticket-code-block,
.ticket-issue-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ticket-label {
  color: #8a948f;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ticket-code {
  color: #178554;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.ticket-issue {
  overflow: hidden;
  color: #2f3c35;
  font-size: 0.92rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-status-block {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
}

.status-pill {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 999px;
  background: #f0f2f1;
  color: #66716b;
  font-size: 0.72rem;
  font-weight: 700;
}

.status-pill.new {
  background: #fff0e8;
  color: #a35220;
}

.status-pill.open,
.status-pill.resolved {
  background: #edf7f1;
  color: #178554;
}

.status-pill.replied {
  background: #edf4fb;
  color: #376c91;
}

.status-pill.pending {
  background: #fff7dc;
  color: #8a6a12;
}

.status-pill.closed {
  background: #f1f2f1;
  color: #89918d;
}

.ticket-age {
  color: #7f8984;
  font-size: 0.78rem;
  white-space: nowrap;
}

.ticket-chevron {
  color: #87918c;
  font-size: 1.25rem;
}

.ticket-filter-section {
  display: flex;
  flex-direction: column;
}

.ticket-filter-bar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  width: 100%;
  padding: 14px;
  box-sizing: border-box;
  border: 1px solid #dfe5e1;
  border-radius: 12px;
  background: #f8faf9;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 180px;
}

.filter-field span {
  color: #7f8984;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.filter-field select {
  min-height: 42px;
  padding: 0 36px 0 12px;
  border: 1px solid #d5ddd8;
  border-radius: 9px;
  outline: 0;
  background: #ffffff;
  color: #29362f;
  font: inherit;
  cursor: pointer;
}

.filter-spacer {
  flex: 1;
}

.get-tickets-button {
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 9px;
  background: #18ab69;
  color: #ffffff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.get-tickets-button:hover:not(:disabled) {
  background: #15945c;
}

.get-tickets-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.clear-filters-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  width: 42px;
  min-height: 42px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: #c94d4d;
  color: #ffffff;
  cursor: pointer;
}

.clear-filters-button:hover:not(:disabled) {
  background: #ad3f3f;
}

.clear-filters-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.clear-filters-button .material-icons-outlined {
  font-size: 1.2rem;
}

@media (max-width: 950px) {
    .metric-grid {
        grid-template-columns:
            repeat(2,
                minmax(0, 1fr));
    }

    .found-client-details {
        grid-template-columns:
            repeat(2,
                minmax(0, 1fr));
    }
}

@media (max-width: 760px) {
  .ticket-row {
    grid-template-columns: 1fr 28px;
  }

  .ticket-code-block,
  .ticket-issue-block,
  .ticket-status-block {
    grid-column: 1;
  }

  .ticket-status-block {
    justify-content: flex-start;
  }

  .ticket-chevron {
    grid-column: 2;
    grid-row: 1 / span 3;
  }

  .ticket-filter-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-field {
    width: 100%;
    min-width: 0;
  }

  .filter-spacer {
    display: none;
  }

  .get-tickets-button {
    width: 100%;
  }
}

@media (max-width: 620px) {
    .client-search-form {
        grid-template-columns: 1fr;
    }

    .search-button {
        width: 100%;
    }

    .found-client-heading {
        flex-direction: column;
    }

    .open-client-button {
        width: 100%;
    }

    .metric-grid,
    .found-client-details {
        grid-template-columns: 1fr;
    }
}
</style>

<style scoped>
.stats-controls { justify-content: space-between; display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.stats-controls button { border: 0; border-radius: 8px; padding: 9px 14px; background: #edf4ef; color: #16794f; cursor: pointer; }
.stats-controls small { color: #75867d; }
.stat-additions { flex: 0 0 100%; display: flex; gap: 12px; justify-content: space-between; border-top: 1px solid #e2e9e5; padding-top: 12px; font-size: .8rem; color: #75867d; }
.stat-additions strong { display: block; margin-top: 4px; color: #16794f; font-size: 1rem; }
</style>

<style scoped>
.stats-summary-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.stats-summary-row .refresh-time { margin-left: auto; }
.stats-footer { margin-top: 12px; }
.review-alerts { display: flex; flex-wrap: wrap; gap: 10px; margin: 0; }
.review-alerts button { border: 0; border-radius: 24px; padding: 9px 16px; color: #a34d19; background: #fff0e5; font-weight: 700; cursor: pointer; }
.stats-explanation { color: #75867d; font-size: .8rem; }
</style>
